//============================================
// GAME CONFIGURATION FILE
//============================================
// All game settings, texts, colors, and behaviors are configured here.
// Modify this file to customize the game without touching other code.

//============================================
// GENERAL GAME SETTINGS
//============================================
export const GAME_CONFIG = {
  // Total number of drafts per level
  draftsPerLevel: 4,

  // Level progression order (array of level IDs)
  // Change this array to use different levels or different order
  levelOrder: [3, 4, 5],

  // Level ID before which the transition animation should appear
  transitionBeforeLevel: 5,

  // Mobile draft display mode: 'modal' or 'notification'
  mobileDraftDisplay: 'notification',

  // Maze size (must be odd number for proper generation)
  mazeSize: 15,

  // Mobile joystick size (in pixels)
  joystickSize: 120,
};

//============================================
// PLAYER SETTINGS
//============================================
export const PLAYER_CONFIG = {
  // Movement speed (desktop)
  speedDesktop: 4.5,

  // Movement speed (mobile)
  speedMobile: 3.5,

  // Player collision radius
  radius: 0.3,

  // Starting position
  startX: 1.5,
  startY: 1.5,
};

//============================================
// ENEMY (FEAR) SETTINGS
//============================================
export const ENEMY_CONFIG = {
  // Number of enemies on mobile
  countMobile: 1,

  // Base number of enemies on desktop (increases with level)
  baseCountDesktop: 1,

  // Maximum enemies on desktop
  maxCountDesktop: 3,

  // Base speed multiplier
  baseSpeed: 1.5,

  // Speed increase per level
  speedPerLevel: 0.2,

  // Speed on mobile
  speedMobile: 1.2,

  // Collision distance with player
  collisionDistance: 0.6,

  // Minimum spawn distance from player
  minSpawnDistance: 6,
};

//============================================
// ABILITY SETTINGS
//============================================
export const ABILITY_CONFIG = {
  // Ability duration (desktop) in milliseconds
  durationDesktop: 2000,

  // Ability duration (mobile) in milliseconds
  durationMobile: 3000,

  // Cooldown time (desktop) in milliseconds
  cooldownDesktop: 7000,

  // Cooldown time (mobile) in milliseconds
  cooldownMobile: 5000,

  // Key to activate ability (desktop)
  activationKey: ' ', // Space key
};

//============================================
// UI TEXT CONTENT
//============================================
export const UI_TEXT = {
  menu: {
    title: "ИГРА",
    subtitle: "САМОСАБОТАЖ",
    systemInitialized: "by Alexey Krivdin",
    startButton: "НАЧАТЬ ИГРУ",
    installButton: "📱 Install App",
    version: "v1.0.0 // Production Build",
    albumComment: "Собери все цитаты из альбома и получи пароль к сайту",
    stats: {
      levels: "Уровни",
      targets: "Цитаты",
      status: "Статус",
      levelsValue: "03",
      targetsValue: "04/уровень",
      statusValue: "Готов",
    },
  },

  hud: {
    levelPrefix: "Уровень",
    draftsLabel: "ЦИТАТЫ",
    timeLabel: "ВРЕМЯ",
    abilityButton: "SPACE",
    abilityActive: "ACTIVE!",
    abilityRecharging: "WAIT...",
    abilityReady: "STOP IT!",
  },

  mobileControls: {
    stopButton: "STOP IT",
    moveLabel: "Move",
  },

  draftModal: {
    title: "Черновик собран",
    continueButton: "Continue",
    tapToClose: "Tap anywhere to close",
  },

  draftNotification: {
    prefix: "[ ЦИТАТА НАЙДЕНА ]",
  },

  portalHint: {
    message: "[Выход найден] :: Дверь открыта",
  },

  gameOver: {
    // Default texts (used when specific fear message doesn't override)
    default: {
      header: "System Failure",
      subheader: "",
      restartButton: "Reboot Level",
      menuButton: "Abort Mission",
    },
  },

  victory: {
    header: "ЙОУ!",
    subtitle: "Ништякс, ты прошел игру",
    message: "Не всё так плохо. Может даже перестанешь портить себе жизнь.",
    passwordLabel: "Держи пароль от сайта:",
    password: "NIKTONENUZHEN",
    clickToCopy: "(кликни чтобы скопировать)",
    passwordCopied: "СКОПИРОВАНО!",
    websiteButton: "Перейти на сайт",
    websiteUrl: "https://krivdin.by/samosabotage2",
    playAgainButton: "ИГРАТЬ СНОВА",
  },

  transition: {
    message: "Пу-пу-пум, а ты на опыте",
  },
};

//============================================
// COLORS & THEME
//============================================
export const COLORS = {
  primary: "#FFD900", // Yellow
  secondary: "#ef4444", // Red
  success: "#10b981", // Green
  warning: "#fbbf24", // Yellow
  background: "#0a0a0a", // Almost black

  // UI colors
  textPrimary: "#ffffff",
  textSecondary: "#9ca3af",
  border: "#374151",

  // Notification colors
  notificationDraft: "#fbbf24", // Yellow for draft notifications
  notificationPortal: "#818cf8", // Indigo for portal notifications
};

//============================================
// FONTS
//============================================
export const FONTS = {
  primary: "monospace",
  secondary: "serif",

  // Font sizes (Tailwind classes)
  sizes: {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl",
    "3xl": "text-3xl",
    "4xl": "text-4xl",
    "6xl": "text-6xl",
    "8xl": "text-8xl",
  },
};

//============================================
// LEVEL DEFINITIONS
//============================================
export const LEVELS = [
  {
    id: 1,
    name: "Deserve u",
    theme: "Комната в старой квартире",
    bgColor: "#2d1b1b",
    wallColor: "#4a2c2a",
    floorColor: "#3d2b29",
    playerColor: "#4ade80",
    fearColor: "#dc2626",
    draftColor: "#fbbf24",
    fearType: "doubt",
    musicSources: [
      "/audio/theme1.mp3",
      "https://storage.googleapis.com/mygame-lesha/%D1%81%D0%BE%D0%BC%D0%BD%D0%B8%D1%82%D0%B5%D0%BB%D1%8C%D0%BD%D1%8B%D0%B8%CC%86%20%5Bmusic%5D.mp3",
      "https://corsproxy.io/?https://storage.googleapis.com/mygame-lesha/%D1%81%D0%BE%D0%BC%D0%BD%D0%B8%D1%82%D0%B5%D0%BB%D1%8C%D0%BD%D1%8B%D0%B8%CC%86%20%5Bmusic%5D.mp3"
    ],
  },
  {
    id: 2,
    name: "Уйти нельзя остаться",
    theme: "Дождливый город",
    bgColor: "#1a1a2e",
    wallColor: "#16213e",
    floorColor: "#0f1419",
    playerColor: "#60a5fa",
    fearColor: "#f59e0b",
    draftColor: "#10b981",
    fearType: "uncertainty",
    musicSources: [
      "/audio/theme2.mp3",
      "https://storage.googleapis.com/mygame-lesha/%D1%81%D0%BE%D0%BC%D0%BD%D0%B8%D1%82%D0%B5%D0%BB%D1%8C%D0%BD%D1%8B%D0%B8%CC%86%20%5Bmusic%5D.mp3",
      "https://corsproxy.io/?https://storage.googleapis.com/mygame-lesha/%D1%81%D0%BE%D0%BC%D0%BD%D0%B8%D1%82%D0%B5%D0%BB%D1%8C%D0%BD%D1%8B%D0%B8%CC%86%20%5Bmusic%5D.mp3"
    ],
  },
  {
    id: 3,
    name: "Ладонями",
    theme: "Летний день на природе",
    bgColor: "#1f2937",
    wallColor: "#374151",
    floorColor: "#111827",
    playerColor: "#34d399",
    fearColor: "#ef4444",
    draftColor: "#fcd34d",
    fearType: "vulnerability",
    musicSources: [
      "/audio/theme3.mp3",
      "https://storage.googleapis.com/mygame-lesha/ladonyami8bit.mp3",
      "https://corsproxy.io/?https://storage.googleapis.com/mygame-lesha/ladonyami8bit.mp3"
    ],
  },
  {
    id: 4,
    name: "Сомнительный",
    theme: "Бар ночью",
    bgColor: "#450a0a",
    wallColor: "#7c2d12",
    floorColor: "#292524",
    playerColor: "#a78bfa",
    fearColor: "#dc2626",
    draftColor: "#f472b6",
    fearType: "judgment",
    musicSources: [
      "/audio/theme4.mp3",
      "https://storage.googleapis.com/mygame-lesha/somnitelny8bit.mp3",
      "https://corsproxy.io/?https://storage.googleapis.com/mygame-lesha/somnitelny8bit.mp3"
    ],
  },
  {
    id: 5,
    name: "Опыт",
    theme: "Лаборатория",
    bgColor: "#0c0a09",
    wallColor: "#1c1917",
    floorColor: "#0a0a0a",
    playerColor: "#06b6d4",
    fearColor: "#b91c1c",
    draftColor: "#84cc16",
    fearType: "burnout",
    musicSources: [
      "/audio/theme5.mp3",
      "https://storage.googleapis.com/mygame-lesha/opyt8bit.mp3",
      "https://corsproxy.io/?https://storage.googleapis.com/mygame-lesha/opyt8bit.mp3"
    ],
  },
];

//============================================
// DRAFT TEXTS (SHARED POOL)
//============================================
// These texts are randomly selected throughout the game session
// and won't repeat until all have been used
export const DRAFT_TEXTS = [
  "Будь осторожен с желаниями",
  "Вообще, если подумать, я счастливый человек",
  "Вы предпочли бы знать причину своей смерти или дату",
  "Знаю, всему своё время",
  "Кто кого не достоин? Кто кого не заслужил?",
  "Можно наловчиться замечать хорошее",
  "Мы лишь короткая вспышка",
  "Надеюсь, хватит сил пройти всё достойно",
  "Но, если подумать, я счастливый человек",
  "Откуда вечно этот самый самосаботаж?",
  "Ошибка новичка - быть не здесь и не сейчас",
  "ошибки и риски липнут ирисками",
  "Переварил и пошел дальше",
  "пу-пу-пум, значит продолжаем дальше",
  "Ты думал на всю жизнь, а получился опыт",
  "ты хочешь делать что-то из слабости или из силы?",
  "Уйти нельзя остаться – поставь запятую",
  "Ууууу, тупой панч",
  "Хотя…",
  "Это не навсегда, это тоже пройдет",
  "Я вечно хочу то, чего нет",
  "Я всему научусь и всё у меня получится",
  "Я не сомневаюсь лишь в том, что я сомневаюсь",
  "Я сомневаюсь",
  "I don't deserve you",
];

//============================================
// FEAR MESSAGES (GAME OVER SCREEN)
//============================================
// Each fear message can have custom texts for all UI elements
// If a property is not specified, it falls back to UI_TEXT.gameOver.default
export const FEAR_MESSAGES = [
  {
    id: 1,
    name: "ты выгорел",
    message: "не вини себя, передохни...",
    header: "СТОП!",
    subheader: "",
    restartButton: "окей, продолжаем",
    menuButton: "психануть и выйти",
  },
  {
    id: 2,
    name: "ты в дофаминовой яме",
    message: "нужен детокс от телефона",
    header: "ОУ НОУ...",
    subheader: "",
    restartButton: "я уже не остановлюсь",
    menuButton: "ухожу в детокс",
  },
  {
    id: 3,
    name: "ты опять загнался",
    message: "может сходи на прогулку?",
    header: "ОМАЙГАД",
    subheader: "",
    restartButton: "сам сходи, дай поиграю",
    menuButton: "сам играй, я в лес",
  },
  {
    id: 4,
    name: "ты вспомнил старую кринж ситуацию",
    message: "ошибка новичка. будь здесь и сейчас",
    header: "УПС",
    subheader: "",
    restartButton: "уже забыл, играем",
    menuButton: "спасибо, день испорчен...",
  },
  {
    id: 5,
    name: "ты словил паничку",
    message: "такое бывает, дыши глубже",
    header: "ТРЕВОГА!",
    subheader: "",
    restartButton: "подышать и продолжить",
    menuButton: "потерять сознание",
  },
  {
    id: 6,
    name: "эх, вспомнил неудачный опыт",
    message: "ну зато опыт!",
    header: "ВЗРЫВ ПАМ БУМ!",
    subheader: "",
    restartButton: "пу-пу-пум, значит продолжаем дальше",
    menuButton: "пакеда",
  }
];

//============================================
// HELPER FUNCTIONS
//============================================

// Get level by ID
export const getLevelById = (id) => {
  return LEVELS.find(level => level.id === id);
};

// Get active levels based on levelOrder configuration
export const getActiveLevels = () => {
  return GAME_CONFIG.levelOrder.map(id => getLevelById(id)).filter(Boolean);
};

// Check if transition should appear before a level
export const shouldShowTransitionBefore = (levelId) => {
  return levelId === GAME_CONFIG.transitionBeforeLevel;
};

// Get total number of active levels
export const getTotalLevels = () => {
  return GAME_CONFIG.levelOrder.length;
};

// Get level index in the active progression (0-based)
export const getLevelIndex = (levelId) => {
  return GAME_CONFIG.levelOrder.indexOf(levelId);
};

// Get next level ID in progression
export const getNextLevelId = (currentLevelId) => {
  const currentIndex = getLevelIndex(currentLevelId);
  if (currentIndex === -1 || currentIndex >= GAME_CONFIG.levelOrder.length - 1) {
    return null;
  }
  return GAME_CONFIG.levelOrder[currentIndex + 1];
};

// Check if level is the last one
export const isLastLevel = (levelId) => {
  return getLevelIndex(levelId) === GAME_CONFIG.levelOrder.length - 1;
};

// Get fear message with defaults applied
export const getFearMessageWithDefaults = (fearMessage) => {
  const defaults = UI_TEXT.gameOver.default;
  return {
    name: fearMessage.name,
    message: fearMessage.message,
    header: fearMessage.header || defaults.header,
    subheader: fearMessage.subheader || defaults.subheader,
    restartButton: fearMessage.restartButton || defaults.restartButton,
    menuButton: fearMessage.menuButton || defaults.menuButton,
  };
};